import { useCallback, useEffect, useRef } from 'react';

import { getDocument } from '@atlaskit/browser-apis';

/** Tracks successful content views and visible dwell for each card opening. */
export const useHoverCardDwell = (
	onDwell?: (durationMs: number, canClaimProject?: boolean) => void,
	onViewed?: (loadTimeMs: number) => void,
): {
	onOpen: () => void;
	onClose: () => void;
	onContentReady: () => void;
	onClaimEligibility: (canClaimProject: boolean | undefined) => void;
} => {
	const callbacks = useRef({ onDwell, onViewed });
	useEffect(() => {
		callbacks.current = { onDwell, onViewed };
	}, [onDwell, onViewed]);
	const session = useRef<{
		emitDwell: typeof onDwell;
		emitViewed: typeof onViewed;
		openedAt: number;
		loadTimeMs?: number;
		viewed: boolean;
		durationMs: number;
		startedAt?: number;
		canClaimProject?: boolean;
	} | null>(null);
	const pause = useCallback(() => {
		const current = session.current;
		if (current?.startedAt !== undefined) {
			current.durationMs += performance.now() - current.startedAt;
			current.startedAt = undefined;
		}
	}, []);
	const resume = useCallback(() => {
		const current = session.current;
		if (
			!current ||
			current.loadTimeMs === undefined ||
			getDocument()?.visibilityState !== 'visible'
		)
			return;
		if (!current.viewed) {
			current.viewed = true;
			current.emitViewed?.(current.loadTimeMs);
		}
		current.startedAt ??= performance.now();
	}, []);
	const onContentReady = useCallback(() => {
		const current = session.current;
		if (!current) return;
		current.loadTimeMs ??= Math.max(0, Math.round(performance.now() - current.openedAt));
		resume();
	}, [resume]);
	const onClose = useCallback(() => {
		pause();
		const current = session.current;
		session.current = null;
		if (current?.viewed) {
			const durationMs = Math.max(0, Math.round(current.durationMs));
			if (current.canClaimProject === undefined) current.emitDwell?.(durationMs);
			else current.emitDwell?.(durationMs, current.canClaimProject);
		}
	}, [pause]);
	const onOpen = useCallback(() => {
		if (session.current || (!callbacks.current.onDwell && !callbacks.current.onViewed)) return;
		session.current = {
			emitDwell: callbacks.current.onDwell,
			emitViewed: callbacks.current.onViewed,
			openedAt: performance.now(),
			viewed: false,
			durationMs: 0,
		};
	}, []);
	const onClaimEligibility = useCallback((canClaimProject: boolean | undefined) => {
		if (session.current) session.current.canClaimProject = canClaimProject;
	}, []);
	const enabled = Boolean(onDwell || onViewed);
	useEffect(() => {
		const ownerDocument = getDocument();
		if (!enabled || !ownerDocument) return;
		const onVisibilityChange = () => {
			pause();
			resume();
		};
		ownerDocument.addEventListener('visibilitychange', onVisibilityChange);
		window.addEventListener('pagehide', onClose);
		return () => {
			ownerDocument.removeEventListener('visibilitychange', onVisibilityChange);
			window.removeEventListener('pagehide', onClose);
			onClose();
		};
	}, [enabled, onClose, pause, resume]);
	return { onOpen, onClose, onContentReady, onClaimEligibility };
};
